/** @jsxImportSource @emotion/react */

import { FC } from "react";
import {
  CSSRulesResolver,
  useCSSRulesWithTheme,
} from "../../../../hooks/useCSSRulesWithTheme";
import { ColorNames, getThemeColorFromAlias, theme } from "../../../../theme";
import Typography from "../../../../components/Typography";
import {
  BasicCoverageTypes,
  Maybe,
  Result,
} from "../../../../graphql/__GENERATED__/types";
import { formatNumber, formatPrice } from "../../../../utils/formatNumber";
import CardOptions, {
  TOverflowMenuOptions,
} from "../../../../components/OverflowMenu";
import { IconNames } from "../../../../components/Icons/styles/iconNames";
import ProviderLogo from "../../../../components/ProviderLogos";
import useDetailsColor from "../../../../hooks/useDetailsColor";

const getCSSRules: CSSRulesResolver<{
  active: boolean;
  themeColor: string;
  editing: boolean;
}> = ({ active, themeColor, editing }) => ({
  container: {
    position: "relative",
    padding: 12.5,
  },
  card: {
    backgroundColor: active ? themeColor : theme.colors.White[100],
    cursor: editing ? "pointer" : "not-allowed",
    padding: 20,
    width: 350,
    border: active ? "2px solid" : "1px solid",
    borderColor: active ? themeColor : theme.colors.Grey[100],
    borderRadius: 15,
    boxShadow: theme.boxShadow.default,
  },
  circle: {
    position: "absolute" as const,
    top: 0,
    right: 0,
    borderRadius: "50%",
    width: 25,
    height: 25,
    border: `1px solid ${theme.colors.Pink[100]}`,
    backgroundColor: theme.colors.White[100],
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    color: theme.colors.Pink[100],
    fontWeight: "bold",
  },
  row: {
    display: "flex",
    alignItems: "start",
    justifyContent: "space-between",
  },
});

interface QuotationCardProps {
  result: Result;
  active: boolean;
  order?: number;
  allowEdition?: boolean;
  editing: boolean;
  onClick: () => void;
  onEdit?: () => void;
  onRemove?: () => void;
}

export const returnCoverageType = (
  coverageType: Maybe<BasicCoverageTypes> | undefined
) => {
  let coverage = "";
  switch (coverageType) {
    case BasicCoverageTypes.Custeio:
      coverage = "Custeio";
      break;
    case BasicCoverageTypes.Produtividade:
      coverage = "Produtividade";
      break;
    case BasicCoverageTypes.RiscoNomeado:
      coverage = "Risco Nomeado";
      break;
    default:
      coverage = "";
  }
  return coverage;
};

const QuotationCard: FC<QuotationCardProps> = ({
  result,
  active,
  order,
  allowEdition,
  editing,
  onClick,
  onEdit,
  onRemove,
}) => {
  const { color } = useDetailsColor();
  const themeColor = getThemeColorFromAlias(color, theme.colors)!;
  const styles = useCSSRulesWithTheme(getCSSRules, {
    active,
    themeColor,
    editing,
  });

  const editionOptions: TOverflowMenuOptions[] = [
    {
      text: "Editar",
      onClick: onEdit,
      iconName: IconNames.Edit,
    },
    {
      text: "Excluir",
      onClick: onRemove,
      iconName: IconNames.Delete,
    },
  ];

  return (
    <div css={styles.container}>
      <div onClick={onClick} css={styles.card}>
        {active && order && <div css={styles.circle}>{order}</div>}
        <div css={styles.row}>
          <ProviderLogo
            name={result.provider!}
            color={active ? ColorNames.WHITE : ColorNames.GREY}
          />
          {allowEdition && <CardOptions options={editionOptions} />}
        </div>
        <div>
          <Typography
            variant="textMedium"
            color={active ? ColorNames.WHITE : ColorNames.GREY}
            component="div"
            align="left"
          >
            {`Produtividade: ${
              result.guaranteedProductivity
                ? formatNumber(result.guaranteedProductivity, 2, 2)
                : "Não aplicável"
            }`}
          </Typography>
          <Typography
            variant="textMedium"
            uppercase
            color={active ? ColorNames.WHITE : ColorNames.GREY}
            component="div"
            align="left"
          >
            LMI: {formatPrice(result.amounts?.lmi)}
          </Typography>
          <div css={styles.row}>
            <Typography
              variant="textMedium"
              align="left"
              component="div"
              color={active ? ColorNames.WHITE : ColorNames.GREY}
            >
              Prêmio: {formatPrice(result.amounts?.totalPremium)}
            </Typography>
            <Typography
              variant="textSmall"
              align="right"
              component="div"
              color={active ? ColorNames.WHITE : ColorNames.GREY}
              uppercase
            >
              {returnCoverageType(result.basicCoverageType)}
            </Typography>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuotationCard;
